/** @jsx jsx */
// eslint-disable-next-line
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { css, jsx } from '@emotion/core'
import { Button, CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { theme } from '../../constants'
import { ProductNames } from '../../constants/payments'
import orderService from '../../services/order'
import userService from '../../services/user'
import urlService from '../../services/url'
import getTrackingMinutes from '../../utils/getTrackingMinutes'
import minTommss from '../../utils/minPlans'
import * as Actions from '../admin/actions'
import { history } from '../App'
import sleep from '../../utils/sleep'

const styles = {}
styles.container = css`
  max-width: 900px;
  margin: auto;
  padding-bottom: 16px;
  text-align: left;
  color: white;
`
styles.header = css`
  background: var(--metrica-green);
  color: white;
  font-size: 24px;
  padding: 20px;
  text-align: center;
`
styles.textBody = css`
  padding-right: 30px;
  padding-left: 30px;
  padding-top: 20px;
`
styles.indent = css`
  margin-top: 0px;
  padding-left: 20px;
`
styles.horizontalLine = css`
  border: 0;
  height: 1px;
  background: #ccc;
  margin: 20px auto;
  width: 66%;
`
styles.loadingContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: white;
  opacity: 0.5;
`
styles.applyKeyContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 24px;
`

styles.applyKeyDescriptionContainer = css`
  display: flex;
  flex-direction: column;
`

styles.button = {
  height: '32px',
  minWidth: '180px',
  textTransform: 'none',
  textWrap: 'nowrap',
  borderRadius: '8px',
  padding: '0px 6px',
  backgroundColor: 'var(--metrica-green)'
}

function UserKeyInfo(props) {
  const [loading, setLoading] = React.useState(false)
  const [applied, setApplied] = React.useState(
    props.product.usages === 0 ||
      props.user.user_keys.find(v => v.user_key.code === props.product.code)
  )

  async function applyKey() {
    try {
      setLoading(true)

      try {
        for (let i = 0; i < props.product.usages; i += 1) {
          await userService.addKey(props.user._id, { key: props.product.code })
        }
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
        if (message) {
          toast.error(message)
        } else {
          console.error(error)
          toast.error(window.I18n.t('addKeyModal.errorMessage'))
        }

        setLoading(false)
        return
      }

      const totalMinutes = getTrackingMinutes(props.user)
      const fetchedUser = await Actions.updateUser(null, false)
      const totalMinutesAfter = getTrackingMinutes(fetchedUser)
      if (totalMinutesAfter > totalMinutes) {
        toast.info(
          window.I18n.t('addKeyModal.minutesMessage', { minutes: minTommss(totalMinutesAfter) })
        )
      } else {
        toast.info(window.I18n.t('addKeyModal.successMessage'))
      }

      setApplied(true)
    } catch (error) {
      console.error(error)
      toast.error(window.I18n.t('addKeyModal.errorMessage'))
    } finally {
      setLoading(false)
    }
  }

  const buttonLabel = window.I18n.t(`successfulPayment.${applied ? 'keyApplied' : 'applyKey'}`)

  return (
    <React.Fragment>
      <div css={styles.applyKeyContainer}>
        <div css={styles.applyKeyDescriptionContainer}>
          <strong>
            {props.product.quantity}x {props.product.name}
          </strong>
          <span>{window.I18n.t('successfulPayment.applyKeyDescription')}</span>
        </div>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading || applied}
          style={{
            ...styles.button,
            color: theme.palette.common.white,
            ...(applied ? { opacity: 0.5, pointerEvents: 'none' } : {})
          }}
          onClick={applyKey}
        >
          {loading ? (
            <CircularProgress color="white" size={24} thickness={4} />
          ) : (
            <span>{buttonLabel}</span>
          )}
        </Button>
      </div>
    </React.Fragment>
  )
}

UserKeyInfo.defaultProps = {}

UserKeyInfo.propTypes = {
  user: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired
}

function SuccessfulPayment(props) {
  const [recurrentProducts, setRecurrentProducts] = React.useState([])
  const [oneTimeProducts, setOneTimeProducts] = React.useState([])

  React.useEffect(() => {
    // Get transaction ID from URL.
    const url = window.location.search
    const keyAndId = url.split('?')[1]
    const [key, id] = keyAndId.split('=')
    if (!id) {
      history.push('/')
      return
    }

    const fetchOrder = async () => {
      let response

      // Retry order fetching if it fails.
      const maxRetries = 10
      let retries = 0
      while (retries < maxRetries) {
        try {
          if (key === 'transaction') {
            response = await orderService.getOrderByTransactionId(id)
          } else {
            response = await orderService.getOrderById(id)
          }
          break
        } catch (error) {
          retries += 1
          await sleep(5000)
        }
      }

      if (retries === maxRetries) {
        toast.error(window.I18n.t('admin.errorContactSupport'))
        return
      }

      // Once we're sure order is retrieved, update user to get the proper subscription info.
      await Actions.updateUser()

      const order = response.data
      const { newRecurrentProducts, newOneTimeProducts } = order.items.reduce(
        (acc, v) => {
          let usages = v.user_key.max_usage - v.user_key.count_usage
          let products
          let productCode

          let product = props.monthlyProducts.find(p => p.priceId === v.id)
          if (product) {
            usages = 1
            products = acc.newRecurrentProducts
            productCode = product.productCode
          } else {
            product = props.yearlyProducts.find(p => p.priceId === v.id)
            if (product) {
              usages = 1
              products = acc.newRecurrentProducts
              productCode = product.productCode
            } else {
              product = props.oneTimeProducts.find(p => p.priceId === v.id)
              if (product) {
                products = acc.newOneTimeProducts
                productCode = product.priceCode
              }
            }
          }

          if (products) {
            const productInfo = ProductNames.find(n => n.value === productCode)

            let name
            if (productInfo.value.includes('minutes')) {
              name = window.I18n.t(`plans.${productInfo.translationKey}`)
            } else {
              let translationKey = 'plans.plan'
              if (product.cycle === 'monthly') {
                translationKey = 'plans.planMonthly'
              } else if (product.cycle === 'yearly') {
                translationKey = 'plans.planYearly'
              } else if (product.cycle === 'half-yearly') {
                translationKey = 'plans.planHalfYearly'
              }

              name = window.I18n.t(translationKey, {
                planName: productInfo.translationKey
                  ? window.I18n.t(`plans.${productInfo.translationKey}`)
                  : productInfo.translation
              })
            }

            products.push({
              name,
              code: v.user_key.code,
              quantity: v.quantity,
              usages
            })
          }

          return acc
        },
        { newRecurrentProducts: [], newOneTimeProducts: [] }
      )

      setRecurrentProducts(newRecurrentProducts)
      setOneTimeProducts(newOneTimeProducts)
    }
    fetchOrder()
  }, [props.monthlyProducts, props.yearlyProducts, props.oneTimeProducts])

  const cloudUrl = urlService.getUrl('cloud')
  const downloadsUrl = urlService.getUrl('downloads')

  return (
    <div css={styles.container}>
      <div css={styles.header}>Metrica Sports | {window.I18n.t('successfulPayment.title')}</div>
      <div css={styles.textBody}>
        <p>{window.I18n.t('successfulPayment.thanks')}</p>

        {recurrentProducts.length === 0 && oneTimeProducts.length === 0 && (
          <React.Fragment>
            <hr css={styles.horizontalLine} />
            <div css={styles.loadingContainer}>
              <CircularProgress size={32} thickness={4} />
            </div>
          </React.Fragment>
        )}

        {oneTimeProducts.length > 0 && (
          <React.Fragment>
            <hr css={styles.horizontalLine} />
            <p>
              <strong>{window.I18n.t('successfulPayment.minutesTitle')}</strong>
            </p>
            <div>
              {oneTimeProducts.map(product => (
                <UserKeyInfo product={product} user={props.user} />
              ))}
            </div>

            <p
              style={{ marginTop: '24px' }}
              dangerouslySetInnerHTML={{
                __html: window.I18n.t('successfulPayment.minutesInfo', {
                  pre: `<a href="${cloudUrl}/settings/groups" target="_blank" rel="noopener noreferrer">`,
                  post: '</a>'
                })
              }}
            />
          </React.Fragment>
        )}

        {recurrentProducts.length > 0 && (
          <React.Fragment>
            <hr css={styles.horizontalLine} />
            <p>
              <strong>{window.I18n.t('successfulPayment.licensesTitle')}</strong>
            </p>
            <p>{window.I18n.t('successfulPayment.licenseActivation1')}</p>
            <p>{window.I18n.t('successfulPayment.licenseActivation2')}</p>
            <div>
              {recurrentProducts.map(product => (
                <UserKeyInfo product={product} user={props.user} />
              ))}
            </div>

            <p
              style={{
                marginTop: '24px',
                marginBottom: '0px'
              }}
            >
              {window.I18n.t('successfulPayment.faq')}
            </p>

            <ul css={styles.indent}>
              <li
                dangerouslySetInnerHTML={{
                  __html: window.I18n.t('successfulPayment.download', {
                    pre: `<a href="${downloadsUrl}" target="_blank" rel="noopener noreferrer">`,
                    post: '</a>'
                  })
                }}
              />
              <li
                dangerouslySetInnerHTML={{
                  __html: window.I18n.t('successfulPayment.settingsCloud', {
                    pre: `<a href="${cloudUrl}/settings" target="_blank" rel="noopener noreferrer">`,
                    post: '</a>'
                  })
                }}
              />
              <ul css={styles.indent}>
                <li>{window.I18n.t('successfulPayment.updateSubscription')}</li>
                <li>{window.I18n.t('successfulPayment.updatePayment')}</li>
                <li>{window.I18n.t('successfulPayment.assignLicenses')}</li>
                <li>{window.I18n.t('successfulPayment.addUsers')}</li>
              </ul>
            </ul>

            <p
              dangerouslySetInnerHTML={{
                __html: window.I18n.t('successfulPayment.renewalInfo', {
                  pre: '<a href="https://metrica-sports.com/help-center/gamecloud/how-to-cancel-subscription" target="_blank" rel="noopener noreferrer">',
                  post: '</a>'
                })
              }}
            />
          </React.Fragment>
        )}

        <hr css={styles.horizontalLine} />
        <p
          dangerouslySetInnerHTML={{
            __html: window.I18n.t('successfulPayment.helpCenter', {
              pre: '<a href="https://help.metrica-sports.com" target="_blank" rel="noopener noreferrer">',
              post: '</a>'
            })
          }}
        />
        <p
          dangerouslySetInnerHTML={{
            __html: window.I18n.t('successfulPayment.technicalSupport', {
              pre: '<a href="https://metrica-sports.com/contact" target="_blank" rel="noopener noreferrer">',
              post: '</a>'
            })
          }}
        />

        <div>{window.I18n.t('successfulPayment.happyPlay')}</div>

        <p>{window.I18n.t('successfulPayment.metricaSportsTeam')}</p>
      </div>
    </div>
  )
}

SuccessfulPayment.defaultProps = {}

SuccessfulPayment.propTypes = {
  user: PropTypes.object.isRequired,
  monthlyProducts: PropTypes.array.isRequired,
  halfYearlyProducts: PropTypes.array.isRequired,
  yearlyProducts: PropTypes.array.isRequired,
  oneTimeProducts: PropTypes.array.isRequired
}

function mapStateToProps(state) {
  return {
    user: state.auth.user,
    monthlyProducts: state.admin.monthlyProducts,
    halfYearlyProducts: state.admin.halfYearlyProducts,
    yearlyProducts: state.admin.yearlyProducts,
    oneTimeProducts: state.admin.oneTimeProducts
  }
}

export default connect(mapStateToProps)(SuccessfulPayment)
